import { Component } from '@theme/component';
import { CartAddEvent, ThemeEvents } from '@theme/events';

const SUCCESS_FEEDBACK_DURATION = 1500;
const ERROR_FEEDBACK_DURATION = 2000;

/**
 * @typedef {{ addButton: HTMLButtonElement, buttonText: HTMLElement, spinner: HTMLElement, liveRegion: HTMLElement }} Refs
 */

/**
 * Adds a swatch-category variant to the cart and keeps the button in sync
 * with cart contents when "restrict once" is enabled.
 *
 * @extends {Component<Refs>}
 */
export class NaSwatchCategoryComponent extends Component {
    /** @type {string[]} */
    requiredRefs = ['addButton', 'buttonText', 'spinner', 'liveRegion'];

    #abortController = new AbortController();

    /** @type {number | null} */
    #feedbackTimeout = null;

    connectedCallback() {
        super.connectedCallback();

        document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
            signal: this.#abortController.signal,
        });

        // The N& cart drawer dispatches its own event after removals and quantity changes.
        document.addEventListener('cart:updated', this.#handleCartUpdate, {
            signal: this.#abortController.signal,
        });

        this.#syncFromCart();
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.#abortController.abort();
        this.#clearFeedbackTimeout();
    }

    updatedCallback() {
        this.#syncFromCart();
    }

    /**
     * @returns {boolean}
     */
    get #restrictOnce() {
        return this.dataset.restrictOnce === 'true';
    }

    #clearFeedbackTimeout() {
        if (this.#feedbackTimeout !== null) {
            clearTimeout(this.#feedbackTimeout);
            this.#feedbackTimeout = null;
        }
    }

    /**
     * @param {string | undefined} text
     */
    #setButtonText(text) {
        if (text) this.refs.buttonText.textContent = text;
    }

    /**
     * @param {string | undefined} text
     */
    #announce(text) {
        if (text) this.refs.liveRegion.textContent = text;
    }

    /**
     * @param {boolean} busy
     */
    #setBusy(busy) {
        this.refs.addButton.setAttribute('aria-busy', busy ? 'true' : 'false');
        this.refs.addButton.classList.toggle('is-loading', busy);
        this.refs.spinner.hidden = !busy;
    }

    /**
     * @returns {Promise<{ item_count: number, items: { variant_id: number }[] }>}
     */
    async #fetchCart() {
        const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });

        if (!response.ok) throw new Error(`Cart read failed: ${response.status}`);

        return response.json();
    }

    /**
     * Section ids of every cart-items component on the page, so the add request
     * returns pre-rendered drawer HTML and no follow-up render is needed.
     * Audit note: intentional cross-component lookup (do not flag) — cart sections
     * live outside this component's subtree.
     * @returns {string[]}
     */
    #getCartSectionIds() {
        return Array.from(document.querySelectorAll('cart-items-component'))
            .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : undefined))
            .filter((id) => typeof id === 'string');
    }

    /**
     * @param {Event} event
     */
    #handleCartUpdate = (event) => {
        // Skip while success/error feedback is visible; the feedback timeout re-syncs.
        if (this.#feedbackTimeout !== null) return;

        const detail = /** @type {{ detail?: { resource?: { items?: { variant_id: number }[] } } }} */ (event).detail;
        this.#syncFromCart(detail?.resource?.items ? detail.resource : undefined);
    };

    /**
     * @param {{ items: { variant_id: number }[] } | undefined} [knownCart] - Cart data already available from an event, to avoid a refetch.
     */
    async #syncFromCart(knownCart) {
        const { addButton } = this.refs;
        const { variantId, variantAvailable, buttonText, soldOutText, inCartText } = addButton.dataset;

        if (variantAvailable !== 'true') {
            addButton.disabled = true;
            this.#setButtonText(soldOutText);
            return;
        }

        if (!this.#restrictOnce || !variantId) {
            addButton.disabled = false;
            this.#setButtonText(buttonText);
            addButton.classList.remove('is-success', 'is-in-cart');
            return;
        }

        try {
            const cart = knownCart ?? (await this.#fetchCart());
            const isInCart = cart.items.some((item) => String(item.variant_id) === String(variantId));

            addButton.disabled = isInCart;
            addButton.classList.toggle('is-in-cart', isInCart);
            addButton.classList.remove('is-success');
            this.#setButtonText(isInCart ? inCartText : buttonText);
        } catch (error) {
            console.error(error);
        }
    }

    /** Adds the category variant to the cart. Called from `on:click`. */
    async addToCart() {
        const { addButton } = this.refs;
        const { variantId, buttonText, addingText, successText, errorText } = addButton.dataset;

        if (!variantId || addButton.disabled) return;

        this.#clearFeedbackTimeout();

        addButton.disabled = true;
        addButton.classList.remove('is-success', 'is-in-cart');
        this.#setBusy(true);
        this.#announce(addingText);

        try {
            const sectionIds = this.#getCartSectionIds();
            const response = await fetch(Theme.routes.cart_add_url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({
                    id: variantId,
                    quantity: 1,
                    sections: sectionIds.join(','),
                    sections_url: window.location.pathname,
                }),
            });

            if (!response.ok) throw new Error(`Swatch add to cart failed: ${response.status}`);

            const result = await response.json();
            const cart = await this.#fetchCart();

            addButton.classList.add('is-success');
            this.#setBusy(false);
            this.#setButtonText(successText);
            this.#announce(successText);

            this.#feedbackTimeout = window.setTimeout(() => {
                this.#feedbackTimeout = null;
                addButton.classList.remove('is-success');
                this.#syncFromCart(cart);
            }, SUCCESS_FEEDBACK_DURATION);

            document.dispatchEvent(
                new CartAddEvent(cart, this.id, {
                    source: 'na-swatches-category',
                    itemCount: cart.item_count,
                    variantId,
                    sections: result.sections,
                })
            );
        } catch (error) {
            console.error(error);
            this.#setBusy(false);
            this.#setButtonText(errorText);
            this.#announce(errorText);
            addButton.disabled = false;

            this.#feedbackTimeout = window.setTimeout(() => {
                this.#feedbackTimeout = null;
                this.#setButtonText(buttonText);
                this.#syncFromCart();
            }, ERROR_FEEDBACK_DURATION);
        }
    }
}

if (!customElements.get('na-swatch-category-component')) {
    customElements.define('na-swatch-category-component', NaSwatchCategoryComponent);
}
